import React from 'react';

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
  /** Compact padding for admin tables, inline sections, home modules */
  variant?: 'default' | 'dense';
  /** Optional brand photograph above the icon (prefer over emoji) */
  imageSrc?: string;
  imageAlt?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  className = '',
  variant = 'default',
  imageSrc,
  imageAlt = '',
}) => {
  const padding = variant === 'dense' ? 'py-8 px-4' : 'py-14 sm:py-16 px-4';
  return (
    <div className={`text-center ${padding} ${className}`} role="status">
      {imageSrc ? (
        <div
          className={`mx-auto overflow-hidden border border-white/[0.06] bg-[#0c0c0c] ${
            variant === 'dense' ? 'mb-4 max-w-xs' : 'mb-6 max-w-sm sm:max-w-md'
          }`}
        >
          <img
            src={imageSrc}
            alt={imageAlt}
            className="w-full aspect-[16/10] object-cover"
            loading="lazy"
            decoding="async"
          />
        </div>
      ) : null}
      {icon && !imageSrc ? (
        <div className={`flex justify-center text-gray-500 ${variant === 'dense' ? 'mb-3' : 'mb-5'}`} aria-hidden>
          {icon}
        </div>
      ) : null}
      <h3
        className={`font-display font-semibold tracking-tight text-white mb-2 ${
          variant === 'dense' ? 'text-base' : 'text-lg sm:text-xl'
        }`}
      >
        {title}
      </h3>
      {description && (
        <p
          className={`text-gray-400 text-sm max-w-md mx-auto leading-relaxed ${
            action ? (variant === 'dense' ? 'mb-4' : 'mb-6') : ''
          }`}
        >
          {description}
        </p>
      )}
      {action && <div className="flex justify-center flex-wrap gap-3">{action}</div>}
    </div>
  );
};

export const CarIcon: React.FC<{ className?: string }> = ({ className = 'w-16 h-16' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.25}
      d="M8 17h.01M16 17h.01M5 11l1.5-4h11L19 11M5 11v6a1 1 0 001 1h1a1 1 0 001-1v-1h8v1a1 1 0 001 1h1a1 1 0 001-1v-6M5 11H3m18 0h-2"
    />
  </svg>
);

export default EmptyState;
